import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  useAppSelector,
  useQuery,
  useAppDispatch,
  useGetProductVersionsQuery,
  getLocalRetentionUser,
  parseRedirectAccountFromHkey,
  updateLocalRedirectObj,
  useUserLoginQuery,
  useGetUserOrgNodesQuery,
  useGetRegionsQuery,
  useGetSystemDataQuery,
  getSafeUrl
} from '@monorepo/shared';
import { Loader, useToaster } from 'rsuite';
import { debugMode, HomeElms, server } from '../config/const';
import { selectOU } from '@monorepo/shared/store/fcOuSlice';
import {
  findRedirectAccount,
  selectAccounts,
  updateRedirectAccount
} from '@monorepo/shared/store/accountSlice';
import {
  selectSessionStatus,
  updateDebugKey
} from '@monorepo/shared/store/authSlice';
import { t } from 'i18next';

export const PrivateRoute = () => {
  let query = useQuery();
  const { state } = useLocation();
  const debug_key = query.get('debug_key') || state?.debug_key;
  const redirect_data = query.get('redirect_data') || state?.redirect_data;
  const obj = parseRedirectAccountFromHkey(redirect_data);
  // Note: redirect_account, redirect_user, access_from, org_id is from VM redirect
  const redirect_account =
    query.get('redirect_account') || state?.redirect_account;
  const access_from = query.get('access_from') || state?.access_from;
  const org_id = query.get('org_id') || state?.org_id;

  const redirect_obj =
    obj?.account_id || redirect_account
      ? {
          account_id: obj?.account_id || redirect_account,
          user_id: obj?.user_id,
          node_id: obj?.node_id,
          org_id: org_id,
          access_from: {
            user_id: obj?.by_user_id || access_from,
            account_id: obj?.by_account_id
          }
        }
      : state?.redirect_obj || {};

  const user = query.get('user') || state?.user || getLocalRetentionUser();
  const sessionStatus = useAppSelector(selectSessionStatus);
  const dispatch = useAppDispatch();
  const ouData = useAppSelector(selectOU);
  const accounts = useAppSelector(selectAccounts);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const globalUser = useUserLoginQuery({
    key: debugMode ? debug_key : undefined,
    server: server
  });
  const ou = useGetUserOrgNodesQuery(
    { server: server, fcName: HomeElms?.fcName, refresh: true },
    {
      skip: !globalUser.isSuccess
    }
  );
  const productVersions = useGetProductVersionsQuery(
    { server: server },
    {
      skip: !globalUser.isSuccess
    }
  );

  const getRegions = useGetRegionsQuery(
    { server: server },
    {
      skip: !globalUser.isSuccess
    }
  );
  const { data: systemData } = useGetSystemDataQuery(
    { server: server },
    {
      skip: !globalUser.isSuccess
    }
  );

  // save redirect info to local storage for debugging purposes
  const logObj = {
    redirect_data: redirect_data,
    redirect_account: redirect_account,
    access_from: access_from
  };
  updateLocalRedirectObj(logObj);
  useEffect(() => {
    if (debug_key) {
      dispatch(updateDebugKey(debug_key));
    }
  }, [debug_key]);

  useEffect(() => {
    if (ou.isSuccess) {
      let redirectObj = redirect_obj;
      if (
        !redirectObj?.account_id &&
        !ouData?.length &&
        Object.keys(accounts).length === 1
      ) {
        const account_id = Object.keys(accounts)?.[0] || -1;
        const account = accounts[account_id];
        redirectObj = {
          account_id: account?.account_id,
          user_id: account?.user_id
        };
        navigate('/organization', {
          state: {
            debug_key: debug_key,
            redirect_obj: redirectObj
          }
        });
      }

      if (Object.keys(accounts)?.length && redirectObj?.account_id) {
        const checkAccount = findRedirectAccount({
          accounts,
          redirect_obj: redirectObj
        });
        dispatch(updateRedirectAccount(checkAccount));
      }
      setIsLoading(false);
    }
  }, [ou.data, redirect_obj]);

  useEffect(() => {
    if (sessionStatus === 'REVOKED') {
      window.open(getSafeUrl(`${server}`), '_self');
    }
  }, [sessionStatus]);

  return (
    <>
      {isLoading ? (
        <Loader center content={t('common.loading')} />
      ) : (
        <Outlet
          context={{
            redirect_obj: redirect_obj
          }}
        />
      )}
    </>
  );
};
